import { useTheme } from "@emotion/react";
import { PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router-dom-v5-compat";

import { getNavIconBackground } from "./navAccentUtils";
import { navAccent, navIconBg } from "./navConstants";

type Props = {
  to: string;
  isExternal?: boolean;
  isActive?: boolean;
  accentColor?: string;
  icon?: ReactNode;
  onClick?: () => void;
};

export const NavLinkRow: React.FC<PropsWithChildren<Props>> = ({
  to,
  isExternal,
  isActive,
  accentColor,
  icon,
  onClick,
  children,
}) => {
  const theme = useTheme();
  const linkColor = accentColor ?? navAccent;
  const inactiveIconBackground = accentColor ? getNavIconBackground(accentColor) : navIconBg;

  const rowCss = {
    display: "flex",
    alignItems: "center",
    gap: "0.625rem",
    padding: "0.375rem 0",
    borderRadius: theme.smallBorderRadius,
    textDecoration: "none",
    color: linkColor,
    "&:hover": { opacity: 0.85 },
  };

  const content = (
    <>
      <span
        css={{
          display: "inline-flex",
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          width: "1.75rem",
          height: "1.75rem",
          borderRadius: "0.375rem",
          background: isActive ? linkColor : inactiveIconBackground,
          color: isActive ? "white" : linkColor,
          fontSize: "0.875rem",
        }}
      >
        {icon}
      </span>
      <span
        css={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "0.875rem",
          fontWeight: isActive ? 700 : 400,
        }}
      >
        {children}
      </span>
    </>
  );

  if (isExternal) {
    return (
      <a href={to} target="_blank" rel="noreferrer" onClick={onClick} css={rowCss}>
        {content}
      </a>
    );
  }

  return (
    <Link to={to} onClick={onClick} css={rowCss}>
      {content}
    </Link>
  );
};
